/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);

      // On initial load: sync availability checkboxes from URL and apply custom rules
      if (this.filteringEnabled) {
        this.syncAvailabilityCheckboxFromUrl();
        this.applyCustomAvailabilityFilter(window.location.search.substring(1));
        this.updateAvailabilityFilterCounts();
      }
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled && this.desktopSortBy) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        // --- CUSTOM AVAILABILITY FACET OVERRIDE ---
        const availabilityFilter = this.querySelector('[data-custom-availability="true"]');
        let availabilityValues = [];

        if (availabilityFilter) {
          const checked = availabilityFilter.querySelectorAll('input[type="checkbox"]:checked');
          availabilityValues = Array.from(checked).map(i => i.value);
        }

        // Remove native Shopify availability filter
        searchParams.delete('filter.v.availability');

        // Add custom availability param instead
        searchParams.delete('custom_availability');
        availabilityValues.forEach(v => {
          searchParams.append('custom_availability', v);
        });
        // --- END CUSTOM AVAILABILITY OVERRIDE ---

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      console.log('applyFilters: ', searchParams);
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        if (closeBtn) {
          closeBtn.ariaDisabled = 'true';
          closeBtn.classList.add('is-loading');
        }

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Safari/iOS performance: Batch DOM updates to reduce reflows
          // Use DocumentFragment for efficient DOM manipulation
          const facetsFragment = document.createDocumentFragment();
          const facetsContent = tmpl.content.getElementById('facets');
          if (facetsContent) {
            while (facetsContent.firstChild) {
              facetsFragment.appendChild(facetsContent.firstChild);
            }
          }
          
          // Clear and update in one operation
          this.form.innerHTML = '';
          this.form.appendChild(facetsFragment);

          // Restore custom availability checkbox states
          const params2 = new URLSearchParams(searchParams);
          const selectedVals = params2.getAll('custom_availability');
          const availFilter = this.form.querySelector('[data-custom-availability="true"]');

          if (availFilter) {
            const inputs = availFilter.querySelectorAll('input[type="checkbox"]');
            inputs.forEach(input => {
              input.checked = selectedVals.includes(input.value);
            });
          }

          // Update the label of the mobile filter button
          const newCloseBtn = tmpl.content.querySelector('.js-close-drawer-mob');
          if (closeBtn && newCloseBtn) {
            closeBtn.innerText = newCloseBtn.innerText;
          }

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? currentResultsUl.getAttribute('class') : this.currentResultsClass;

          // Safari/iOS performance: Batch results update
          const resultsFragment = document.createDocumentFragment();
          const resultsContent = tmpl.content.getElementById('filter-results');
          if (resultsContent) {
            while (resultsContent.firstChild) {
              resultsFragment.appendChild(resultsContent.firstChild);
            }
          }
          
          this.results.innerHTML = '';
          this.results.appendChild(resultsFragment);

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Fix pagination links so they preserve all current params (including custom_availability)
          this.updatePaginationLinks(searchParams);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          if (closeBtn) {
            closeBtn.classList.remove('is-loading');
            closeBtn.removeAttribute('aria-disabled');
          }

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });

          // Apply custom availability filtering
          this.applyCustomAvailabilityFilter(searchParams);

          // Hide pagination if there is only one page of results
          this.fixPaginationBasedOnTrueCounts(searchParams);

          // Update availability filter counts with all products
          this.updateAvailabilityFilterCounts();  

          // Update the applied availability filters in the DOM
          this.updateAppliedAvailabilityFilters(searchParams);
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Applies custom availability filtering based on inventory threshold.
     * - "In stock" (value '1') → totalInventory > threshold
     * - "Out of stock" (value '0') → totalInventory <= threshold AND has ETA
     * @param {string} searchParams - Current search parameters
     */
    applyCustomAvailabilityFilter(searchParams) {
      const params = new URLSearchParams(searchParams);
      const availabilityValues = params.getAll('custom_availability');

      const hasInStock = availabilityValues.includes('1');
      const hasOutOfStock = availabilityValues.includes('0');

      // No filter or both filters selected → show all
      if (availabilityValues.length === 0 || (hasInStock && hasOutOfStock)) {
        this.showAllProducts();
        return;
      }

      const settings = window.availabilityFilterSettings || { inventoryThreshold: 0 };
      const threshold = parseInt(settings.inventoryThreshold, 10) || 0;

      const cards = this.results.querySelectorAll('[data-total-inventory]');

      cards.forEach(card => {
        const totalInventory = parseInt(card.dataset.totalInventory || '0', 10);
        const hasEta = (card.dataset.hasEta === 'true');
        const listItem = card.closest('.js-pagination-result') || card;

        let shouldShow = false;

        if (hasOutOfStock) {
          // OUT OF STOCK RULE: threshold or below AND MUST have ETA
          shouldShow = (totalInventory <= threshold) && hasEta;
        }

        if (hasInStock) {
          // IN STOCK RULE: above threshold only (ETA irrelevant)
          shouldShow = (totalInventory > threshold);
        }

        if (shouldShow) {
          listItem.style.display = '';
          listItem.removeAttribute('data-availability-filtered');
        } else {
          listItem.style.display = 'none';
          listItem.setAttribute('data-availability-filtered', 'true');
        }
      });
    }

    /**
     * Shows all products (removes custom availability filtering).
     */
    showAllProducts() {
      const filteredItems = this.results.querySelectorAll('[data-availability-filtered]');
      filteredItems.forEach((item) => {
        item.style.display = '';
        item.removeAttribute('data-availability-filtered');
      });
    }

    /**
     * Sync availability checkboxes from URL on initial load (for full page loads, incl. pagination).
     */
    syncAvailabilityCheckboxFromUrl() {
      if (!this.form) return;
      const params = new URLSearchParams(window.location.search.substring(1));
      const selected = params.getAll('custom_availability');
      const availFilter = this.form.querySelector('[data-custom-availability="true"]');
      if (!availFilter) return;

      const inputs = availFilter.querySelectorAll('input[type="checkbox"]');
      inputs.forEach(input => {
        input.checked = selected.includes(input.value);
      });
    }

    /**
     * Ensures pagination links preserve all current filter/sort params (including custom_availability).
     * @param {string} searchParams - Current search params string.
     */
    updatePaginationLinks(searchParams) {
      const params = new URLSearchParams(searchParams);
      const paginationLinks = this.results.querySelectorAll('.pagination a, .js-pagination a');

      paginationLinks.forEach(link => {
        try {
          const url = new URL(link.href);
          const pageParam = url.searchParams.get('page');

          const newUrl = new URL(window.location.origin + window.location.pathname);

          // Copy all current params
          params.forEach((value, key) => {
            newUrl.searchParams.append(key, value);
          });

          // Make sure page is set correctly
          if (pageParam) {
            newUrl.searchParams.set('page', pageParam);
          }

          link.href = newUrl.toString();
        } catch (e) {
          // ignore invalid URLs
        }
      });
    }

    /**
     * Hides the pagination if there is only one page of results.
     */
    fixPaginationBasedOnTrueCounts(searchParams) {
      const pagination = this.results.querySelector('.pagination, .js-pagination');
      if (!pagination) return;
    
      const params = new URLSearchParams(searchParams);
      const values = params.getAll('custom_availability');
    
      const hasInStock = values.includes('1');
      const hasOutOfStock = values.includes('0');
    
      // If both selected → show ALL products → pagination should follow native Shopify
      if (values.length === 0 || (hasInStock && hasOutOfStock)) {
        pagination.style.display = '';
        return;
      }
    
      // Use cached true counts
      const counts = this.availabilityCounts || this.getCachedAvailabilityCounts();
      if (!counts) return;
    
      const perPage = this.results.querySelectorAll('.js-pagination-result').length;
    
      let trueCount = 0;
    
      if (hasInStock) {
        trueCount = counts.inStockCount;
      } else if (hasOutOfStock) {
        trueCount = counts.outOfStockCount;
      }
    
      // Correct Rule:
      // If true result count is <= per page → hide pagination
      // Otherwise → show
      if (trueCount <= perPage) {
        pagination.style.display = 'none';
      } else {
        pagination.style.display = '';
      }
    }
    
    
    /**
     * Updates the applied availability filters in the DOM.
     * @param {string} searchParams - Current search parameters.
     */
    updateAppliedAvailabilityFilters(searchParams) {
      const params = new URLSearchParams(searchParams);
      const selected = params.getAll('custom_availability');
      const activeContainer = this.activeFiltersList;
      if (!activeContainer) return;
    
      // Remove old custom availability entries
      activeContainer.querySelectorAll('.active-filter--availability').forEach(el => el.remove());
    
      if (selected.length === 0) return;
    
      // Translation: label mapping
      const labels = {
        '0': 'Out of stock',
        '1': 'In stock'
      };
    
      selected.forEach(val => {
        const el = document.createElement('a');
        el.href = this.removeAvailabilityFromUrl(params, val);
        el.className = 'active-filter active-filter--availability flex items-center text-current';
        el.innerHTML = `
          ${document.querySelector('[data-custom-availability="true"] summary .flex-auto')?.textContent || 'Availability'}:
          ${labels[val] || val}
          <span class="ml-1">${'<svg><!-- close icon placeholder --></svg>'}</span>
        `;
        activeContainer.appendChild(el);
      });
    }
    
    /**
     * Removes an availability filter from the URL.
     * @param {URLSearchParams} params - Current search parameters.
     * @param {string} valueToRemove - The value to remove.
     * @returns {string} The new URL with the availability filter removed.
     */
    removeAvailabilityFromUrl(params, valueToRemove) {
      const newParams = new URLSearchParams(params);
      const selected = newParams.getAll('custom_availability');
    
      // Remove that one value
      const newSelected = selected.filter(v => v !== valueToRemove);
    
      newParams.delete('custom_availability');
      newSelected.forEach(v => newParams.append('custom_availability', v));
    
      return `${window.location.pathname}?${newParams.toString()}`;
    }
    


    /**
     * Sets the availability filter counts to loading state.
     */
    setAvailabilityCountsLoading() {
      const availabilityFilter = this.querySelector('[data-custom-availability="true"]');
      if (!availabilityFilter) return;

      const countSpans = availabilityFilter.querySelectorAll('.filter__count');
      countSpans.forEach((span) => {
        span.textContent = '';
        span.classList.add('filter__count--loading', 'is-loading');
      });
    }

    /**
     * Gets the storage key for caching availability counts per collection.
     */
    getAvailabilityCountsCacheKey() {
      const pathParts = window.location.pathname.split('/');
      const collectionIndex = pathParts.indexOf('collections');
      if (collectionIndex !== -1 && pathParts[collectionIndex + 1]) {
        return `availabilityCounts_${pathParts[collectionIndex + 1]}`;
      }
      return null;
    }

    /**
     * Gets cached availability counts from sessionStorage.
     */
    getCachedAvailabilityCounts() {
      const cacheKey = this.getAvailabilityCountsCacheKey();
      if (!cacheKey) return null;

      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          return JSON.parse(cached);
        }
      } catch (e) {
        // sessionStorage not available or parse error
      }
      return null;
    }

    /**
     * Saves availability counts to sessionStorage.
     */
    setCachedAvailabilityCounts(counts) {
      const cacheKey = this.getAvailabilityCountsCacheKey();
      if (!cacheKey) return;

      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(counts));
      } catch (e) {
        // sessionStorage not available
      }
    }

    /**
     * Updates the availability filter count elements in the DOM with cached values.
     */
    updateAvailabilityCountsDOM() {
      const availabilityFilter = this.querySelector('[data-custom-availability="true"]');
      const counts = this.availabilityCounts || this.getCachedAvailabilityCounts();
      if (!availabilityFilter || !counts) return;

      const { inStockCount, outOfStockCount } = counts;

      const filterValues = availabilityFilter.querySelectorAll('input[type="checkbox"]');
      filterValues.forEach((input) => {
        const label = input.nextElementSibling;
        if (!label) return;

        const countSpan = label.querySelector('.filter__count');
        if (!countSpan) return;

        // Remove loading state
        countSpan.classList.remove('filter__count--loading', 'is-loading');

        if (input.value === '1') {
          countSpan.textContent = `(${inStockCount})`;
        } else if (input.value === '0') {
          countSpan.textContent = `(${outOfStockCount})`;
        }
      });
    }

    /**
     * Fetches all products from the collection and updates availability filter counts.
     * Uses Section Rendering API to get product cards with inventory data across all pages.
     * Only fetches once per collection - subsequent calls use cached counts from sessionStorage.
     */
    async updateAvailabilityFilterCounts() {
      const availabilityFilter = this.querySelector('[data-custom-availability="true"]');
      if (!availabilityFilter) return;

      // Check memory cache first
      if (this.availabilityCounts) {
        this.updateAvailabilityCountsDOM();
        return;
      }

      // Check sessionStorage cache (persists across page navigations)
      const cachedCounts = this.getCachedAvailabilityCounts();
      if (cachedCounts) {
        this.availabilityCounts = cachedCounts;
        this.updateAvailabilityCountsDOM();
        return;
      }

      // Set loading state before fetching
      this.setAvailabilityCountsLoading();

      const settings = window.availabilityFilterSettings || { inventoryThreshold: 0 };
      const threshold = settings.inventoryThreshold;

      // Get the section ID for Section Rendering API
      const sectionId = this.form?.dataset.filterSectionId;
      if (!sectionId) return;

      try {
        let inStockCount = 0;
        let outOfStockCount = 0;
        let page = 1;
        let hasMore = true;

        // Fetch all pages using Section Rendering API
        while (hasMore) {
          const fetchUrl = `${window.location.pathname}?page=${page}&section_id=${sectionId}`;
          const response = await fetch(fetchUrl);

          if (!response.ok) break;

          const html = await response.text();
          const tmpl = document.createElement('template');
          tmpl.innerHTML = html;

          // Find all product cards in this page
          const productCards = tmpl.content.querySelectorAll('.card--product[data-total-inventory]');

          if (productCards.length === 0) {
            hasMore = false;
            break;
          }

          // Count products based on inventory threshold
          productCards.forEach((card) => {
            const totalInventory = parseInt(card.dataset.totalInventory || '0', 10);

            // In stock if: inventory > threshold
            // Out of stock if: inventory <= threshold
            if (totalInventory > threshold) {
              inStockCount++;
            } else {
              outOfStockCount++;
            }
          });

          page++;

          // Check if there's a next page link
          const pagination = tmpl.content.querySelector('.js-pagination');
          const nextArrow = tmpl.content.querySelector('.pagination__arrow--next:not([aria-disabled])');
          hasMore = (pagination?.dataset.isMoreResults === 'true') || (nextArrow !== null);
        }

        // Cache the counts in memory and sessionStorage
        this.availabilityCounts = { inStockCount, outOfStockCount };
        this.setCachedAvailabilityCounts(this.availabilityCounts);

        // Update the DOM
        this.updateAvailabilityCountsDOM();

        console.log(`Availability counts fetched and cached - In Stock: ${inStockCount}, Out of Stock: ${outOfStockCount}, Threshold: ${threshold}`);
      } catch (error) {
        console.warn('Error fetching collection products for availability counts:', error);
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
